// client/src/components/creditAssignment/AdminCreditAssignmentCompanyDetail.js
//
// Admin: dettaglio di un'azienda abilitata alla cessione del credito.
// Qui l'admin gestisce le anagrafiche clienti (nome cliente + plafond).
// "Già utilizzato" e "rimanente" sono calcolati dal backend.

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Users, Plus } from 'lucide-react';
import {
  formatCurrencyWithSymbol,
  formatCurrencyInput,
  parseCurrencyInput,
  handleCurrencyInputChange
} from '../utils/formatUtils';
import { fetchClients, createClient, updateClient } from './creditAssignmentApi';

const emptyForm = { clientName: '', plafond: '' };

const AdminCreditAssignmentCompanyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [companyName, setCompanyName] = useState('');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null); // null = nessun form, 'new' = creazione

  const loadClients = useCallback(() => {
    fetchClients(id)
      .then(setClients)
      .catch(() => setError(t('creditAssignment.errors.fetchClients')))
      .finally(() => setLoading(false));
  }, [id, t]);

  useEffect(() => {
    axios
      .get(`/api/admin/organizations/${id}`)
      .then((r) => setCompanyName(r.data.organization?.name || r.data.name || ''))
      .catch(() => {});
    loadClients();
  }, [id, loadClients]);

  const openCreate = () => {
    setEditingId('new');
    setForm(emptyForm);
    setError('');
  };

  const openEdit = (client) => {
    setEditingId(client.id);
    setForm({ clientName: client.clientName, plafond: formatCurrencyInput(String(client.plafond)) });
    setError('');
  };

  const cancelForm = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    setError('');
    const plafondNum = parseCurrencyInput(form.plafond);
    if (!form.clientName.trim()) return setError(t('creditAssignment.errors.clientNameRequired'));
    if (!Number.isFinite(plafondNum) || plafondNum < 0)
      return setError(t('creditAssignment.errors.invalidPlafond'));

    setSaving(true);
    try {
      if (editingId === 'new') {
        await createClient({ company: id, clientName: form.clientName.trim(), plafond: plafondNum });
      } else {
        await updateClient(editingId, { clientName: form.clientName.trim(), plafond: plafondNum });
      }
      cancelForm();
      loadClients();
    } catch (err) {
      setError(err.response?.data?.message || t('creditAssignment.errors.saveClient'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">{t('common.loading')}</div>;

  return (
    <div className="company-dashboard">
      <div className="page-navigation" style={{ marginBottom: '14px' }}>
        <button
          type="button"
          className="back-btn"
          onClick={() => navigate('/admin/credit-assignment/companies')}
        >
          ← {t('creditAssignment.admin.backToCompanies')}
        </button>
      </div>

      <div className="card">
        <div className="card-title">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Users className="card-title-icon" size={20} />
            <span>
              {t('creditAssignment.admin.clientsFor')} {companyName || '—'}
            </span>
          </div>
          {editingId === null && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={openCreate}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              <Plus size={14} />
              {t('creditAssignment.admin.addClient')}
            </button>
          )}
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {editingId !== null && (
          <form
            className="request-form"
            style={{ margin: '16px 0', padding: '16px', background: '#f7f9fc', borderRadius: '8px' }}
            onSubmit={submitForm}
          >
            <div className="form-group">
              <label htmlFor="ca-client-name">
                {t('creditAssignment.clients.name')} <span className="req">*</span>
              </label>
              <input
                id="ca-client-name"
                type="text"
                value={form.clientName}
                onChange={(e) => setForm({ ...form, clientName: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="ca-client-plafond">
                {t('creditAssignment.clients.plafond')} <span className="req">*</span>
              </label>
              <input
                id="ca-client-plafond"
                type="text"
                inputMode="decimal"
                value={form.plafond}
                onChange={(e) => handleCurrencyInputChange(e, setForm, form, 'plafond')}
                required
              />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? t('common.loading') : t('common.save')}
              </button>
              <button type="button" className="btn btn-light" onClick={cancelForm} disabled={saving}>
                {t('common.cancel')}
              </button>
            </div>
          </form>
        )}

        <table>
          <thead>
            <tr>
              <th>{t('creditAssignment.clients.name')}</th>
              <th>{t('creditAssignment.clients.plafond')}</th>
              <th>{t('creditAssignment.clients.used')}</th>
              <th>{t('creditAssignment.clients.remaining')}</th>
              <th>{t('common.action')}</th>
            </tr>
          </thead>
          <tbody>
            {clients.length > 0 ? (
              clients.map((c) => (
                <tr key={c.id}>
                  <td>{c.clientName}</td>
                  <td>{formatCurrencyWithSymbol(c.plafond, 'EUR')}</td>
                  <td>
                    {formatCurrencyWithSymbol(c.usedAmount, 'EUR')}{' '}
                    <span style={{ color: '#6c757d' }}>({(c.usedPercentage || 0).toFixed(1)}%)</span>
                  </td>
                  <td>
                    {formatCurrencyWithSymbol(c.remainingAmount, 'EUR')}{' '}
                    <span style={{ color: '#6c757d' }}>({(c.remainingPercentage || 0).toFixed(1)}%)</span>
                  </td>
                  <td>
                    <button type="button" className="view-btn" onClick={() => openEdit(c)}>
                      {t('common.edit')}
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5">{t('creditAssignment.clients.none')}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminCreditAssignmentCompanyDetail;
